import {
  takeEvery,
  put,
  call,
  select,
  fork,
  take,
} from "@redux-saga/core/effects";
import { AnyAction } from "redux";
import { channel } from "redux-saga";
import {
  EnumApprovalStatus,
  EnumDataTransferStatus,
  EnumInspectionQuestionStatus,
  EnumInspectionStandardStatus,
  EnumPageMode,
  EnumInspectionQuestionState,
} from "../../../common/enums/common.enums";
import {
  InspectionQuestionDTO,
  QuestionnaireDTO,
  RemoveInspectionQuestionDTO,
  UploadFileDTO,
} from "../../../common/types/common.dto.types";
import { ActionResponseType } from "../../../common/types/common.types";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import {
  APPROVEORREJECTINSPECTIONQUESTIONANSWER,
  GETINSPECTIONQUESTION,
  INSPECTIONFILEUPLOAD,
  REMOVEINSPECTIONQUESTION,
  SAVEINSPECTIONQUESTIONANSWERS,
  SAVEINSPECTIONSTANDARDSTATUS,
  removeInspectionQuestion,
  CHECKINSPECTIONQUESTIONSTATUS,
  getSiteInspectionQuestiontData,
} from "./QuestionnairePage.action";
import {
  updateQuestionAnswerApprovalStatus,
  getSiteInspectionQuestiontDataApi,
  inspectionFileUpload,
  saveInspectionQuestionAnswer,
  saveInspectionStandardStatus,
  removeInspectionQuestionApi,
  checkInspectionQuestionStatusIsRemoved,
} from "./QuestionnairePage.api";
import {
  setInspectionQuestionRemoveStatus,
  setIsCheckedRemovedInComplete,
  setPageMode,
  setPageModeRedirectMode,
  setSiteInspectionQuestionnaire,
  updateQuestionnaireSaveRunningStatus,
  updateUploadFilesList,
  updateUploadFileStatus,
} from "./QuestionnairePage.reducer";
import { QuestionnaireDataType } from "./QuestionnairePage.types";
import { QuestionnaireSummaryPageParametersType } from "../QuestionnaireSummaryPage/QuestionnaireSummaryPage.types";
import { RedirectObject } from "../../../common/types/common.page.type";
import { SiteInspectionStartPageParametersType } from "../SiteInspectionStartPage/SiteInspectionStartPage.types";
import { setLoading } from "../../layouts/main-layout/MainLayout.reducer";
import { RootState } from "../../../core/store";
import { QuestionnaireStartPageParametersTypes } from "../QuestionnaireStartPage/QuestionnaireStartPage.types";

/**
 * A custom Redux Saga channel used for navigation-related actions.
 */
const navigationChannel = channel();

/**
 * A custom Redux Saga channel used for save site inspections actions.
 */
const saveSiteInspectionChannel = channel();

/**
 * A custom Redux Saga channel used for get site inspections actions.
 */
const getSiteInspectionChannel = channel();

/**
 * A custom Redux Saga channel used for reducer-related actions.
 */
const reducerChannel = channel();

/**
 * Watches the custom navigation channel and dispatches any actions received.
 */
export function* navigationChannelWatcher() {
  while (true) {
    const action = yield take(navigationChannel);
    yield put(action);
  }
}

/**
 * Watches the custom navigation channel and dispatches save site inspection.
 */
export function* saveSiteInspectionChannelWatcher() {
  while (true) {
    const payload = yield take(saveSiteInspectionChannel);

    // Forward to your saga watcher
    yield put({
      type: SAVEINSPECTIONQUESTIONANSWERS,
      payload,
    });
  }
}

/**
 * Watches the custom navigation channel and dispatches get site inspection.
 */
export function* getSiteInspectionChannelWatcher() {
  while (true) {
    const payload = yield take(getSiteInspectionChannel);

    // Forward to your saga watcher
    yield put({
      type: GETINSPECTIONQUESTION,
      payload,
    });
  }
}

/**
 * Watches the custom navigation channel and dispatches any actions received.
 */
export function* reducerChannelWatcher() {
  while (true) {
    const action = yield take(reducerChannel);
    yield put(action);
  }
}

const getSiteInspectionQuestionWatcher = function* (): any {
  yield takeEvery(GETINSPECTIONQUESTION, function* (action: AnyAction): any {
    // set page loading on
    yield put(setLoading(true));
    // get data from backend

    const SearchParam: QuestionnaireDTO = action.payload;

    const state: QuestionnaireDataType = yield select(
      (s: RootState) => s.siteInspectionQuestionnaireReducer
    );

    let toBeUploaded: Array<UploadFileDTO> =
      state.Questionnaire.UploadedFileList.filter(
        (file) =>
          file.UploadStatus === EnumDataTransferStatus.NotStarted ||
          file.UploadStatus === EnumDataTransferStatus.InProgress
      );
    if (toBeUploaded.length > 0) {
      yield put(
        updateQuestionnaireSaveRunningStatus({
          QuestionnaireSaveActionName: GETINSPECTIONQUESTION,
          IsQuestionnaireSaveRunning: true,
          QuestionnaireSaveCopy: SearchParam,
        } as QuestionnaireDataType)
      );
    } else {
      try {
        const result: ActionResponseType = yield call(() =>
          getSiteInspectionQuestiontDataApi(SearchParam)
        );
        if (!result.IsSuccess) {
          yield put(setLoading(false));
          if (result.Data.InspectionQuestion.ErrorStatus > 0) {
            yield put(
              openPopup({
                Open: true,
                BodyText: result.ErrorMessage
                  ? result.ErrorMessage
                  : "An error occurred when saving data",
                HeaderText: "Warning",
                PopupType: EnumPopupType.WarningPopup,
                OkBtnClick: () => {
                  // Trigger channel for this payload when OK is clicked
                  if (
                    result.Data.InspectionQuestion.ErrorStatus ===
                    EnumInspectionQuestionState.AlreadyRemovedQuestion
                  ) {
                    getSiteInspectionChannel.put({
                      ...SearchParam,
                      SaveAnswerBeforeClickPrevious: false,
                    });
                  } else if (
                    result.Data.InspectionQuestion.ErrorStatus ===
                      EnumInspectionQuestionState.InspectionCompleted ||
                    result.Data.InspectionQuestion.ErrorStatus ===
                      EnumInspectionQuestionState.InspectionStandardCompleted
                  ) {
                    if (SearchParam.SelectedSourceType === 0) {
                      navigationChannel.put(
                        getSiteInspectionQuestiontData({
                          StandardID: SearchParam.StandardID,
                          SiteInspectionID: SearchParam.SiteInspectionID,
                          IsSiteInspectionViewButtonClick: true,
                          InspectionStandardId:
                            SearchParam.InspectionStandardId,
                          QuestionnaireStarted:
                            SearchParam.QuestionnaireStarted,
                          ReferenceID: SearchParam.ReferenceID,
                          SelectedSourceType: SearchParam.SelectedSourceType,
                          SelectedSources: SearchParam.SelectedSources,
                          InspectionStandardName:
                            SearchParam.InspectionStandardName,
                        } as QuestionnaireDTO)
                      );
                    } else {
                      let redirectObject = {
                        NavigateURl: "/siteInspectionStart",
                        NavigateObject: {
                          state: {
                            InspectionId: SearchParam.SiteInspectionID,
                          } as SiteInspectionStartPageParametersType,
                        },
                      } as RedirectObject;

                      navigationChannel.put(
                        setPageModeRedirectMode(redirectObject)
                      );
                    }
                  } else if (
                    result.Data.InspectionQuestion.ErrorStatus ===
                    EnumInspectionQuestionState.AllQuestionRemoved
                  ) {
                    let redirectObject = {
                      NavigateURl: "/siteInspectionStart",
                      NavigateObject: {
                        state: {
                          InspectionId: SearchParam.SiteInspectionID,
                        } as SiteInspectionStartPageParametersType,
                      },
                    } as RedirectObject;

                    navigationChannel.put(
                      setPageModeRedirectMode(redirectObject)
                    );
                  } else if (
                    result.Data.InspectionQuestion.ErrorStatus ===
                    EnumInspectionQuestionState.InspectionRemoved
                  ) {
                    let redirectObject = {
                      NavigateURl: "/siteInspections",
                    } as RedirectObject;

                    navigationChannel.put(
                      setPageModeRedirectMode(redirectObject)
                    );
                  }
                },
              } as PopupProps)
            );
          } else {
            yield put(
              openPopup({
                Open: true,
                BodyText: "No inspection question data to show",
                HeaderText: "Warning",
                PopupType: EnumPopupType.WarningPopup,
              } as PopupProps)
            );
          }
        } else {
          if (result === null) {
            yield put(setLoading(false));
            yield put(
              openPopup({
                Open: true,
                BodyText: "No inspection question data to show",
                HeaderText: "Warning",
                PopupType: EnumPopupType.SuccessPopup,
                // okBtnClick:  ()=>navigate("/standards", {}),
              } as PopupProps)
            );
          } else {
            // yield put(clearPageState());
            const inspectionQuestionnaireDTO: QuestionnaireDTO = result.Data;

            if (
              inspectionQuestionnaireDTO.IsSiteInspectionViewButtonClick ||
              inspectionQuestionnaireDTO.InspectionQuestion.InspectionStandard
                ?.InspectionStandardStatusId ===
                EnumInspectionStandardStatus.Completed
            ) {
              yield put(setPageMode(EnumPageMode.ViewMode));
              inspectionQuestionnaireDTO.ReadOnlyMode = true;

              if (
                inspectionQuestionnaireDTO.IsFromQuestionnairePge &&
                inspectionQuestionnaireDTO.SelectedSourceType === 0
              ) {
                yield put(
                  openPopup({
                    Open: true,
                    BodyText:
                      "Inspection already been completed by another user.",
                    HeaderText: "Warning",
                    PopupType: EnumPopupType.WarningPopup,
                    // okBtnClick: () => navigate("/standards", {}),
                  } as PopupProps)
                );
              } else if (inspectionQuestionnaireDTO.SelectedSourceType > 0) {
                let redirectObject = {
                  NavigateURl: "/questionnaireStart",
                  NavigateObject: {
                    state: {
                      StandardId: inspectionQuestionnaireDTO.StandardID,
                      InspectionId: inspectionQuestionnaireDTO.SiteInspectionID,
                      InspectionStandardId:
                        inspectionQuestionnaireDTO.InspectionStandardId,
                      Title: inspectionQuestionnaireDTO.InspectionStandardName,
                      IsContinueButtonVisible: true,
                    } as QuestionnaireStartPageParametersTypes,
                  },
                } as RedirectObject;

                yield put(setPageModeRedirectMode(redirectObject));
              }
            } else if (inspectionQuestionnaireDTO.IsEnableReviewByGlobalSME) {
              if (
                inspectionQuestionnaireDTO.ReadOnlyMode ||
                (inspectionQuestionnaireDTO.InspectionQuestion?.IsAnswered ??
                  false)
              ) {
                yield put(setPageMode(EnumPageMode.ViewMode));
                inspectionQuestionnaireDTO.ReadOnlyMode = true;
              } else {
                yield put(setPageMode(EnumPageMode.AddMode));
                inspectionQuestionnaireDTO.ReadOnlyMode = false;
              }
            } else {
              yield put(setPageMode(EnumPageMode.AddMode));
              inspectionQuestionnaireDTO.ReadOnlyMode = false;
            }

            yield put(
              setSiteInspectionQuestionnaire({
                ...inspectionQuestionnaireDTO,
                RejectedNow: false,
              })
            );
            yield put(setLoading(false));
          }
        }
      } catch (error) {
        yield put(setLoading(false));
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
            // okBtnClick: () => navigate("/standards", {}),
          } as PopupProps)
        );
      }
    }

    // set page loading on
    // yield put(setLoading(false));
  });
};

const saveSiteInspectionQuestionWatcher = function* (): any {
  yield takeEvery(
    SAVEINSPECTIONQUESTIONANSWERS,
    function* (action: AnyAction): any {
      // set page loading
      yield put(setLoading(true));
      const InspectionQuestionAnswer: QuestionnaireDTO = action.payload;
      const state: QuestionnaireDataType = yield select(
        (s: RootState) => s.siteInspectionQuestionnaireReducer
      );
      let toBeUploaded: Array<UploadFileDTO> =
        state.Questionnaire.UploadedFileList.filter(
          (file) =>
            file.UploadStatus === EnumDataTransferStatus.NotStarted ||
            file.UploadStatus === EnumDataTransferStatus.InProgress
        );

      if (toBeUploaded.length > 0) {
        yield put(
          updateQuestionnaireSaveRunningStatus({
            QuestionnaireSaveActionName: SAVEINSPECTIONQUESTIONANSWERS,
            IsQuestionnaireSaveRunning: true,
            QuestionnaireSaveCopy: InspectionQuestionAnswer,
          } as QuestionnaireDataType)
        );
      } else {
        let toBeUploaded: Array<UploadFileDTO> =
          state.Questionnaire.UploadedFileList.filter(
            (file) =>
              file.UploadStatus === EnumDataTransferStatus.NotStarted ||
              file.UploadStatus === EnumDataTransferStatus.InProgress
          );
        try {
          // save Subject data from backend

          const result: ActionResponseType = yield call(() =>
            saveInspectionQuestionAnswer(InspectionQuestionAnswer)
          );

          if (!result.IsSuccess) {
            if (result.Data.InspectionQuestion.ErrorStatus > 0) {
              yield put(
                openPopup({
                  Open: true,
                  BodyText: result.ErrorMessage
                    ? result.ErrorMessage
                    : "An error occurred when saving data",
                  HeaderText: "Warning",
                  PopupType: EnumPopupType.WarningPopup,
                  OkBtnClick: () => {
                    // Trigger channel for this payload when OK is clicked
                    if (
                      result.Data.InspectionQuestion.ErrorStatus ===
                      EnumInspectionQuestionState.AlreadyRemovedQuestion
                    ) {
                      saveSiteInspectionChannel.put({
                        ...InspectionQuestionAnswer,
                        SaveAnswerBeforeClickPrevious: false,
                      });
                    } else if (
                      result.Data.InspectionQuestion.ErrorStatus ===
                        EnumInspectionQuestionState.InspectionCompleted ||
                      result.Data.InspectionQuestion.ErrorStatus ===
                        EnumInspectionQuestionState.InspectionStandardCompleted
                    ) {
                      if (InspectionQuestionAnswer.SelectedSourceType === 0) {
                        navigationChannel.put(
                          getSiteInspectionQuestiontData({
                            StandardID: InspectionQuestionAnswer.StandardID,
                            SiteInspectionID:
                              InspectionQuestionAnswer.SiteInspectionID,
                            IsSiteInspectionViewButtonClick: true,
                            InspectionStandardId:
                              InspectionQuestionAnswer.InspectionStandardId,
                            QuestionnaireStarted:
                              InspectionQuestionAnswer.QuestionnaireStarted,
                            ReferenceID: InspectionQuestionAnswer.ReferenceID,
                            SelectedSourceType:
                              InspectionQuestionAnswer.SelectedSourceType,
                            SelectedSources:
                              InspectionQuestionAnswer.SelectedSources,
                            InspectionStandardName:
                              InspectionQuestionAnswer.InspectionStandardName,
                          } as QuestionnaireDTO)
                        );
                      } else {
                        let redirectObject = {
                          NavigateURl: "/siteInspectionStart",
                          NavigateObject: {
                            state: {
                              InspectionId:
                                InspectionQuestionAnswer.SiteInspectionID,
                            } as SiteInspectionStartPageParametersType,
                          },
                        } as RedirectObject;

                        navigationChannel.put(
                          setPageModeRedirectMode(redirectObject)
                        );
                      }
                    } else if (
                      result.Data.InspectionQuestion.ErrorStatus ===
                      EnumInspectionQuestionState.AllQuestionRemoved
                    ) {
                      let redirectObject = {
                        NavigateURl: "/siteInspectionStart",
                        NavigateObject: {
                          state: {
                            InspectionId:
                              InspectionQuestionAnswer.SiteInspectionID,
                          } as SiteInspectionStartPageParametersType,
                        },
                      } as RedirectObject;

                      navigationChannel.put(
                        setPageModeRedirectMode(redirectObject)
                      );
                    } else if (
                      result.Data.InspectionQuestion.ErrorStatus ===
                      EnumInspectionQuestionState.InspectionRemoved
                    ) {
                      let redirectObject = {
                        NavigateURl: "/siteInspections",
                      } as RedirectObject;

                      navigationChannel.put(
                        setPageModeRedirectMode(redirectObject)
                      );
                    }
                  },
                } as PopupProps)
              );
            } else {
              yield put(
                openPopup({
                  Open: true,
                  BodyText: result.ErrorMessage
                    ? result.ErrorMessage
                    : "An error occurred when saving data",
                  HeaderText: "Warning",
                  PopupType: EnumPopupType.WarningPopup,
                } as PopupProps)
              );
            }
          } else {
            const inspectionQuestionnaireDTO: QuestionnaireDTO = result.Data;

            if (
              !inspectionQuestionnaireDTO.IsSiteInspectionViewButtonClick &&
              inspectionQuestionnaireDTO.InspectionQuestion.InspectionStandard
                ?.InspectionStandardStatusId !==
                EnumInspectionStandardStatus.Completed
            ) {
              yield put(setPageMode(EnumPageMode.AddMode));
              inspectionQuestionnaireDTO.ReadOnlyMode = false;
            }

            yield put(
              setSiteInspectionQuestionnaire({
                ...inspectionQuestionnaireDTO,
                RejectedNow: false,
              })
            );

            if (inspectionQuestionnaireDTO.IsEnableReviewByGlobalSME) {
              if (
                inspectionQuestionnaireDTO.ReadOnlyMode ||
                inspectionQuestionnaireDTO.IsSiteInspectionViewButtonClick
              ) {
                yield put(setPageMode(EnumPageMode.ViewMode));
              } else {
                yield put(setPageMode(EnumPageMode.EditMode));
              }
            }
          }
        } catch (error) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "An error occurred when saving data",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
              // okBtnClick: () => navigate("/standards", {}),
            } as PopupProps)
          );
        }

        // set page loading
        yield put(setLoading(false));
      }
    }
  );
};

const saveSiteInspectionStandardStatusWatcher = function* (): any {
  yield takeEvery(
    SAVEINSPECTIONSTANDARDSTATUS,
    function* (action: AnyAction): any {
      // set page loading
      yield put(setLoading(true));
      const InspectionStandardStatusParam: QuestionnaireDTO = action.payload;

      const state: QuestionnaireDataType = yield select(
        (s: RootState) => s.siteInspectionQuestionnaireReducer
      );

      let toBeUploaded: Array<UploadFileDTO> =
        state.Questionnaire.UploadedFileList.filter(
          (file) =>
            file.UploadStatus === EnumDataTransferStatus.NotStarted ||
            file.UploadStatus === EnumDataTransferStatus.InProgress
        );
      if (toBeUploaded.length > 0) {
        yield put(
          updateQuestionnaireSaveRunningStatus({
            QuestionnaireSaveActionName: SAVEINSPECTIONSTANDARDSTATUS,
            IsQuestionnaireSaveRunning: true,
            QuestionnaireSaveCopy: InspectionStandardStatusParam,
          } as QuestionnaireDataType)
        );
      } else {
        // use for page navigation
        try {
          // save Subject data from backend

          const result: ActionResponseType = yield call(() =>
            saveInspectionStandardStatus(InspectionStandardStatusParam)
          );

          if (!result.IsSuccess) {
            yield put(
              openPopup({
                Open: true,
                BodyText: result.ErrorMessage
                  ? result.ErrorMessage
                  : "An error occurred when saving data",
                HeaderText: "Warning",
                PopupType: EnumPopupType.WarningPopup,
              } as PopupProps)
            );
          } else {
            //yield put(setLoading(false));

            const inspectionQuestionnaireDTO: QuestionnaireDTO = result.Data;
            if (
              inspectionQuestionnaireDTO.IsFromQuestionnairePge &&
              inspectionQuestionnaireDTO.SelectedSourceType === 0
            ) {
              yield put(setPageMode(EnumPageMode.ViewMode));
              inspectionQuestionnaireDTO.ReadOnlyMode = true;
              yield put(
                openPopup({
                  Open: true,
                  BodyText:
                    "Inspection already been completed by another user.",
                  HeaderText: "Warning",
                  PopupType: EnumPopupType.WarningPopup,
                  // okBtnClick: () => navigate("/standards", {}),
                } as PopupProps)
              );

              yield put(
                setSiteInspectionQuestionnaire({
                  ...inspectionQuestionnaireDTO,
                  RejectedNow: false,
                })
              );
            } else {
              if (
                InspectionStandardStatusParam.InspectionStandardStatus ===
                  EnumInspectionStandardStatus.Paused &&
                !inspectionQuestionnaireDTO.IsReferenceButtonClick
              ) {
                let redirectObject = {
                  NavigateURl: "/siteInspectionStart",
                  NavigateObject: {
                    state: {
                      InspectionId:
                        InspectionStandardStatusParam.SiteInspectionID,
                    } as SiteInspectionStartPageParametersType,
                  },
                } as RedirectObject;

                yield put(setPageModeRedirectMode(redirectObject));
              } else if (
                InspectionStandardStatusParam.InspectionStandardStatus ===
                  EnumInspectionStandardStatus.Completed &&
                !inspectionQuestionnaireDTO.IsReferenceButtonClick
              ) {
                let redirectObject = {
                  NavigateURl: "/questionnaireSummary",
                  NavigateObject: {
                    state: {
                      StandardId: InspectionStandardStatusParam.StandardID,
                      SiteInspectionId:
                        InspectionStandardStatusParam.SiteInspectionID,
                      ReAssess: false,
                      InspectionStandardId:
                        InspectionStandardStatusParam.InspectionStandardId,
                    } as QuestionnaireSummaryPageParametersType,
                  },
                } as RedirectObject;

                yield put(setPageModeRedirectMode(redirectObject));
              } else if (
                inspectionQuestionnaireDTO.SelectedSourceType > 0 &&
                inspectionQuestionnaireDTO.IsReferenceButtonClick
              ) {
                let redirectObject = {
                  NavigateURl: "/questionnaireStart",
                  NavigateObject: {
                    state: {
                      StandardId: InspectionStandardStatusParam.StandardID,
                      InspectionId:
                        InspectionStandardStatusParam.SiteInspectionID,
                      InspectionStandardId:
                        InspectionStandardStatusParam.InspectionStandardId,
                      Title:
                        InspectionStandardStatusParam.InspectionStandardName,
                      IsContinueButtonVisible: true,
                    } as QuestionnaireStartPageParametersTypes,
                  },
                } as RedirectObject;

                yield put(setPageModeRedirectMode(redirectObject));
              }
            }
          }
        } catch (error) {
          yield put(
            openPopup({
              Open: true,
              BodyText: "An error occurred when saving data",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
            } as PopupProps)
          );
        }

        // set page loading
        yield put(setLoading(false));
      }
    }
  );
};

const inspectionFileUploadWatcher = function* (): any {
  const fileuploading = yield takeEvery(
    INSPECTIONFILEUPLOAD,
    function* (): any {
      // get questionnaire page state from reducer

      try {
        let toBeUploaded: Array<UploadFileDTO> = [] as Array<UploadFileDTO>;
        let inpogressUploaded: UploadFileDTO = {} as UploadFileDTO;

        do {
          const state: QuestionnaireDataType = yield select(
            (s: RootState) => s.siteInspectionQuestionnaireReducer
          );

          // check file is in uploading progress
          inpogressUploaded = state.Questionnaire.UploadedFileList.find(
            (file) => file.UploadStatus === EnumDataTransferStatus.InProgress
          );
          if (!inpogressUploaded) {
            toBeUploaded = state.Questionnaire.UploadedFileList.filter(
              (file) => file.UploadStatus === EnumDataTransferStatus.NotStarted
            );
            if (toBeUploaded && toBeUploaded.length > 0) {
              // create temp upload file list
              let tempUploadFileList: Array<UploadFileDTO> = [
                ...state.Questionnaire.UploadedFileList,
              ] as Array<UploadFileDTO>;

              // select next file index to be uploading
              let nextToBeUploadedFileIndex: number =
                tempUploadFileList.findIndex(
                  (file) =>
                    file.UploadStatus === EnumDataTransferStatus.NotStarted
                );

              // update next file to be uploading status to inprogress
              tempUploadFileList[nextToBeUploadedFileIndex] = {
                ...tempUploadFileList[nextToBeUploadedFileIndex],
                UploadStatus: EnumDataTransferStatus.InProgress,
              };

              // update ui status of next file to be uploading
              yield put(updateUploadFilesList(tempUploadFileList));

              const result = yield call(
                async () =>
                  await inspectionFileUpload(
                    tempUploadFileList[nextToBeUploadedFileIndex]
                  )
              );

              if (!result.IsSuccess) {
              } else {
                yield put(
                  updateUploadFileStatus({
                    ...result.Data,
                    UploadStatus: EnumDataTransferStatus.Finished,
                  })
                );
              }
            }
          }
        } while (toBeUploaded.length > 0);
        const state: QuestionnaireDataType = yield select(
          (s: RootState) => s.siteInspectionQuestionnaireReducer
        );

        if (state.IsQuestionnaireSaveRunning) {
          yield put({
            type: state.QuestionnaireSaveActionName,
            payload: {
              ...state.QuestionnaireSaveCopy,
              UploadedFileList: state.Questionnaire.UploadedFileList,
            },
          });

          yield put(
            updateQuestionnaireSaveRunningStatus({
              QuestionnaireSaveActionName: "",
              IsQuestionnaireSaveRunning: false,
              QuestionnaireSaveCopy: {} as QuestionnaireDTO,
            } as QuestionnaireDataType)
          );
        }
      } catch (error) {}
    }
  );

  // yield takeLatest(QUESTIONNAIREPAGESTOPFILEUPLOAD, function* () {
  //   yield cancel(fileuploading);
  // });
};

const updateQuestionAnswerApprovalStatusWatcher = function* (): any {
  yield takeEvery(
    APPROVEORREJECTINSPECTIONQUESTIONANSWER,
    function* (action: AnyAction): any {
      // set page loading
      yield put(setLoading(true));

      const questionAnswerApproveOrReject: InspectionQuestionDTO =
        action.payload;

      try {
        // save Subject data from backend

        const result: ActionResponseType = yield call(() =>
          updateQuestionAnswerApprovalStatus(questionAnswerApproveOrReject)
        );

        if (!result.IsSuccess) {
          yield put(
            openPopup({
              Open: true,
              BodyText: result.ErrorMessage
                ? result.ErrorMessage
                : "An error occurred when saving data",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
            } as PopupProps)
          );
        } else {
          const inspectionQuestionnaireDTO: QuestionnaireDTO = {
            ...result.Data,
            ReadOnlyMode:
              questionAnswerApproveOrReject.AnswerApprovalStatus ===
              EnumApprovalStatus.Reject
                ? false
                : result.Data?.ReadOnlyMode,
            RejectedNow:
              questionAnswerApproveOrReject.AnswerApprovalStatus ===
              EnumApprovalStatus.Reject,
          };

          yield put(setSiteInspectionQuestionnaire(inspectionQuestionnaireDTO));

          if (
            questionAnswerApproveOrReject.AnswerApprovalStatus ===
            EnumApprovalStatus.Reject
          ) {
            yield put(setPageMode(EnumPageMode.EditMode));
          } else {
            yield put(setPageMode(EnumPageMode.ViewMode));
          }
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when saving data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
            // okBtnClick: () => navigate("/standards", {}),
          } as PopupProps)
        );
      }

      // set page loading
      yield put(setLoading(false));
    }
  );
};

const removeInspectionQuestionWatcher = function* (): any {
  yield takeEvery(REMOVEINSPECTIONQUESTION, function* (action: AnyAction): any {
    // set page loading
    yield put(setLoading(true));

    const removequestion: RemoveInspectionQuestionDTO = action.payload;

    try {
      const result: ActionResponseType = yield call(() =>
        removeInspectionQuestionApi(removequestion)
      );

      const responseType: EnumInspectionQuestionState = result.Data;

      if (!result.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText:
              result.ErrorMessage ??
              "An unexpected error occurred while removing the inspection question.",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
            OkBtnClick() {
              if (
                responseType ===
                EnumInspectionQuestionState.AlreadyRemovedQuestion
              ) {
                navigationChannel.put(
                  setInspectionQuestionRemoveStatus(
                    EnumInspectionQuestionStatus.Removed
                  )
                );
              } else if (
                responseType === EnumInspectionQuestionState.AllQuestionRemoved
              ) {
                let redirectObject = {
                  NavigateURl: "/questionnaireStart",
                  NavigateObject: {
                    state: {
                      StandardId: removequestion.StandardID,
                      InspectionId: removequestion.InspectionId,
                      InspectionStandardId: removequestion.InspectionStandardId,
                      Title: removequestion.InspectionStandardName,
                      IsContinueButtonVisible: true,
                    } as QuestionnaireStartPageParametersTypes,
                  },
                } as RedirectObject;

                navigationChannel.put(setPageModeRedirectMode(redirectObject));
              } else if (
                responseType ===
                  EnumInspectionQuestionState.InspectionCompleted ||
                responseType ===
                  EnumInspectionQuestionState.InspectionStandardCompleted
              ) {
                if (removequestion.SelectedSourceType === 0) {
                  navigationChannel.put(
                    getSiteInspectionQuestiontData({
                      StandardID: removequestion.StandardID,
                      SiteInspectionID: removequestion.SiteInspectionID,
                      IsSiteInspectionViewButtonClick: true,
                      InspectionStandardId: removequestion.InspectionStandardId,
                      QuestionnaireStarted: removequestion.QuestionnaireStarted,
                      ReferenceID: removequestion.ReferenceID,
                      SelectedSourceType: removequestion.SelectedSourceType,
                      SelectedSources: removequestion.SelectedSources,
                      InspectionStandardName:
                        removequestion.InspectionStandardName,
                    } as QuestionnaireDTO)
                  );
                } else {
                  let redirectObject = {
                    NavigateURl: "/siteInspectionStart",
                    NavigateObject: {
                      state: {
                        InspectionId: removequestion.SiteInspectionID,
                      } as SiteInspectionStartPageParametersType,
                    },
                  } as RedirectObject;

                  navigationChannel.put(
                    setPageModeRedirectMode(redirectObject)
                  );
                }
              }
            },
          } as PopupProps)
        );
      } else {
        if (
          responseType === EnumInspectionQuestionState.RemediationTaskExists
        ) {
          yield put(
            openPopup({
              Open: true,
              BodyText: `A task has already been created in the Remediation Tracker for this question. 
              Deleting this question will also remove the associated remediation tasks, and this action cannot be recovered.<br/><br/>
              Do you want to Proceed?`,
              HeaderText: "Confirmation",
              PopupType: EnumPopupType.YesNoConfirmation,
              YesBtnClick() {
                navigationChannel.put(
                  removeInspectionQuestion({
                    ...removequestion,
                    IsRemeditaionTrackerTaskExist: true,
                  })
                );
              },
              DisableBackdropClose: true,
            } as PopupProps)
          );
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText: "Question removed successfully.",
              HeaderText: "Success",
              PopupType: EnumPopupType.SuccessPopup,
              OkBtnClick() {
                navigationChannel.put(
                  setInspectionQuestionRemoveStatus(
                    EnumInspectionQuestionStatus.Removed
                  )
                );
              },
            } as PopupProps)
          );
        }
      }
    } catch (error) {
      yield put(
        openPopup({
          Open: true,
          BodyText:
            "An unexpected error occurred while removing the inspection question.",
          HeaderText: "Warning",
          PopupType: EnumPopupType.WarningPopup,
          // okBtnClick: () => navigate("/standards", {}),
        } as PopupProps)
      );
    }

    // set page loading
    yield put(setLoading(false));
  });
};

const checkInspectionQuestionStatusWatcher = function* (): any {
  yield takeEvery(
    CHECKINSPECTIONQUESTIONSTATUS,
    function* (action: AnyAction): any {
      const question: InspectionQuestionDTO = action.payload;

      try {
        const result: ActionResponseType = yield call(() =>
          checkInspectionQuestionStatusIsRemoved(question)
        );

        if (!result.IsSuccess) {
          yield put(
            openPopup({
              Open: true,
              BodyText: result.ErrorMessage
                ? result.ErrorMessage
                : "An error occurred when saving data",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
            } as PopupProps)
          );
        } else {
          if (result.Data == true) {
            yield put(
              openPopup({
                Open: true,
                BodyText: result.InfoMessage,
                HeaderText: "Warning",
                PopupType: EnumPopupType.WarningPopup,
                OkBtnClick() {
                  navigationChannel.put(
                    setInspectionQuestionRemoveStatus(
                      EnumInspectionQuestionStatus.Removed
                    )
                  );
                  reducerChannel.put(setIsCheckedRemovedInComplete(true));
                },
              } as PopupProps)
            );
          } else {
            reducerChannel.put(setIsCheckedRemovedInComplete(true));
          }
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText:
              "An unexpected error occurred while removing the inspection question.",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      }
    }
  );
};

const SiteInspectionQuestionnairePageSaga = function* () {
  // yield all([
  //   getSiteInspectionQuestionWatcher(),
  //   saveSiteInspectionQuestionWatcher(),
  //   saveSiteInspectionStandardStatusWatcher(),
  //   inspectionFileUploadWatcher(),
  // ]);
  // yield all([
  //   fork(getSiteInspectionQuestionWatcher),
  //   fork(saveSiteInspectionQuestionWatcher),
  //   fork(saveSiteInspectionStandardStatusWatcher),
  //   fork(inspectionFileUploadWatcher),
  // ]);
  yield fork(getSiteInspectionQuestionWatcher);
  yield fork(saveSiteInspectionQuestionWatcher);
  yield fork(saveSiteInspectionStandardStatusWatcher);
  yield fork(inspectionFileUploadWatcher);
  yield fork(updateQuestionAnswerApprovalStatusWatcher);
  yield fork(removeInspectionQuestionWatcher);
  yield fork(navigationChannelWatcher);
  yield fork(saveSiteInspectionChannelWatcher);
  yield fork(getSiteInspectionChannelWatcher);
  yield fork(checkInspectionQuestionStatusWatcher);
  yield fork(reducerChannelWatcher);
};

export default SiteInspectionQuestionnairePageSaga;
